<template>
	<div class="px-4 sm:px-6 lg:px-6 py-6 bg-gray-100 flex-grow flex flex-col">
		<h2 class="text-2xl font-semibold text-gray-700 mb-6">
			{{ __("Your daily prices for location") }} {{ user.address.zip }}
			{{ user.address.city }}
		</h2>

		<!-- to do:
             merge desktop and mobile table to avoid duplicate code -->
		<!-- DESKTOP -->
		<div
			class="shadow rounded-lg overflow-hidden sm:rounded-xl bg-white mb-6 hidden md:block"
		>
			<table class="min-w-full divide-y divide-gray-300">
				<tbody class="divide-y divide-gray-200">
					<tr v-for="(item, index) in bestPrices" v-bind:key="index">
						<td
							class="whitespace-nowrap pl-4 py-4 text-gray-700 text-2xl font-semibold"
						>
							{{
								__(
									$cropOptions.getLabel(
										item.offer.product__grade,
										$cropOptions.productGradeOptions
									)
								)
							}}
						</td>
						<td
							class="whitespace-nowrap pl-4 py-4 text-gray-500 text-xl font-semibold"
						>
							{{ item.offer.product__harvest }}
						</td>

						<td class="pl-4 py-4 text-gray-500 text-xl font-semibold">
							{{ renderDate(item) }}
						</td>

						<td class="whitespace-nowrap pl-4 py-4 text-gray-500 price-td">
							<div class="price text-gray-700 font-bold text-right">
								<span class="text-4xl">
									{{
										(1 * item.price__mt).toLocaleString(locale, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})
									}}</span
								>
								<span class="font-semibold">€/t</span>
							</div>
							<div
								class="text-xs flex items-center justify-end"
								v-if="item.price__commodity_future_id"
							>
								<span
									class="inline-block bg-orange-500 rounded-full w-2 h-2 mr-1.5"
								></span>
								<span class="inline-block">{{ __("Premium based") }}</span>
							</div>
							<div class="text-xs text-right" v-else>
								{{ __("Fixed price") }}
							</div>
						</td>
						<td
							class="whitespace-nowrap pl-4 pr-4 py-4 text-gray-500 text-right"
						>
							<div v-if="serviceAvailable">
								<ui-button
									type="secondary"
									v-if="item.thread_id != null"
									v-on:click="
										$inertia.visit(
											$route('threads', { active_id: item.thread_id })
										)
									"
								>
									{{ __("To negotiation") }}
								</ui-button>
								<ui-button
									v-else
									v-on:click="
										$bus.$emit(
											'toggle-iframe-modal',
											$route('bid.create', {
												targetType: 'BUY',
												targetId: item.offer.id,
												sourcePriceIndicationId: item.id,
												lastDisplayedPrice: item.price__mt,
											}) + '&iframe_modal=true'
										)
									"
								>
									{{ __("Offer quantity") }}
								</ui-button>
							</div>
							<div v-else>
								{{ __("Last Price") }}
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- MOBILE -->
		<div
			class="shadow rounded-lg overflow-hidden sm:rounded-xl bg-white mb-6 md:hidden"
		>
			<div class="bg-white w-full shadow rounded-lg md:overflow-hidden">
				<ul role="list">
					<li
						class="p-4 hover:bg-gray-50 active:bg-gray-150 border-gray-200 border-b flex justify-between gap-x-3"
						v-for="(item, index) in bestPrices"
						v-bind:key="index"
					>
						<!-- left column -->
						<div>
							<!-- title: grade and harvest year -->
							<div class="flex justify-between items-center space-x-4 w-full">
								<div class="flex items-center gap-x-3 text-xl">
									<div class="text-gray-700 text-2xl font-semibold">
										{{
											__(
												$cropOptions.getLabel(
													item.offer.product__grade,
													$cropOptions.productGradeOptions
												)
											)
										}}
									</div>
									<div class="text-gray-500 text-xl font-semibold">
										{{ item.offer.product__harvest }}
									</div>
								</div>
							</div>

							<!-- date -->
							<p class="">{{ renderDate(item) }}</p>
						</div>

						<!-- right column -->
						<div class="space-y-2">
							<div>
								<div class="price text-gray-700 font-bold text-right">
									<span class="text-4xl">
										{{
											(1 * item.price__mt).toLocaleString(locale, {
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											})
										}}</span
									>
									<span class="font-semibold">€/t</span>
								</div>
								<div
									class="text-xs flex items-center justify-end"
									v-if="item.price__commodity_future_id"
								>
									<span
										class="inline-block bg-orange-500 rounded-full w-2 h-2 mr-1.5"
									></span>
									<span class="inline-block">Prämienpreis</span>
								</div>
								<div class="text-xs text-right" v-else>Festpreis</div>
							</div>

							<div v-if="serviceAvailable">
								<ui-button
									type="secondary"
									v-if="item.thread_id != null"
									v-on:click="
										$inertia.visit(
											$route('threads', { active_id: item.thread_id })
										)
									"
								>
									{{ __("To negotiation") }}
								</ui-button>
								<ui-button
									v-else
									v-on:click="
										$bus.$emit(
											'toggle-iframe-modal',
											$route('bid.create', {
												targetType: 'BUY',
												targetId: item.offer.id,
												sourcePriceIndicationId: item.id,
												lastDisplayedPrice: item.price__mt,
											}) + '&iframe_modal=true'
										)
									"
								>
									Menge offerieren
								</ui-button>
							</div>
							<div v-else>
								{{ __("Last Price") }}
							</div>
						</div>
					</li>
				</ul>
			</div>
		</div>

		<content-price-indications-info />
	</div>
</template>

<script>
export default {
	props: {
		serviceAvailable: {
			type: Boolean,
			required: true,
		},
		user: {
			type: Object,
			required: true,
		},
		filter: {
			type: Object,
			required: true,
		},
		priceIndications: {
			type: Array,
			required: true,
		},
	},
	computed: {
		bestPrices() {
			let that = this;
			let priceOrder = this.$cropOptions.articleCustomSortOrder;

			let prices = this.priceIndications.filter(function (priceIndication) {
				// unless the filter "bestOffers" is "1", we show all price indications
				if (that.filter.bestOffers == "1") {
					return priceIndication.is_best_price == true;
				} else {
					return true;
				}
			});
			return prices.sort((a, b) => {
				if (
					priceOrder[a.offer.product__grade] >
					priceOrder[b.offer.product__grade]
				) {
					return 1;
				} else if (
					priceOrder[a.offer.product__grade] <
					priceOrder[b.offer.product__grade]
				) {
					return -1;
				} else if (
					priceOrder[a.offer.product__grade] ==
					priceOrder[b.offer.product__grade]
				) {
					if (a.price_group > b.price_group) {
						return 1;
					} else if (a.price_group < b.price_group) {
						return -1;
					} else if (a.price_group == b.price_group && a.is_best_price) {
						return 1;
					}
				}
			});
		},
		contactPersonUrl: () => window.baseUrl + "/images/philip-rounded.png",
	},
	methods: {
		renderDate(row) {
			let that = this,
				ret = that.$cropOptions.getLocalizedLabel(
					row.offer.date__type,
					that.$cropOptions.dateModeOptions
				),
				m = [
					row.offer.date__month_from
						? that.$cropOptions.dateToMonthLocalizedString(
								row.offer.date__month_from
						  )
						: null,
					row.offer.date__month_to
						? that.$cropOptions.dateToMonthLocalizedString(
								row.offer.date__month_to
						  )
						: null,
				]
					.filter((e) => e != null)
					.join(" - ");
			if (m) {
				ret = ret + " " + m;
			}
			return ret;
		},
	},
};
</script>

<style scoped>
.updated-price .price {
	animation: pulse-animation-green-bg 1.5s;
	animation-delay: 1s;
}

.updated-price i {
	animation: pulse-animation-green 1s;
	visibility: visible;
}

@keyframes pulse-animation-green-bg {
	0% {
		color: rgb(150, 189, 13);
		background: rgba(150, 189, 13, 0.2);
		box-shadow: 0 0 0 0px rgba(150, 189, 13, 0.2);
	}

	100% {
		color: inherit;
		background: rgba(0, 0, 0, 0);
		box-shadow: 0 0 0 7px rgba(0, 0, 0, 0);
	}
}

@keyframes pulse-animation-green {
	0% {
		background: rgba(150, 189, 13, 1);
		box-shadow: 0 0 0 0px rgba(150, 189, 13, 1);
	}

	20% {
		background: rgba(150, 189, 13, 0.5);
		box-shadow: 0 0 0 2px rgba(150, 189, 13, 0.5);
	}

	40% {
		background: rgba(150, 189, 13, 1);
		box-shadow: 0 0 0 0px rgba(150, 189, 13, 1);
	}

	60% {
		background: rgba(150, 189, 13, 0.5);
		box-shadow: 0 0 0 2px rgba(150, 189, 13, 0.5);
	}

	100% {
		background: rgba(150, 189, 13, 0);
		box-shadow: 0 0 0 0 rgba(150, 189, 13, 0);
	}
}
</style>
